import type { GamePlayData, GameSessionPageData, InstantWinDraw } from '@/types'
import { sortPrizes } from '@/utils/instant-win-utils'
import { delay } from '@/utils/utils'

import { GameLocalSession } from './game-session-local'

const GAME_SESSION_ID = 'demo'

/**
 * Creates a demo game session for a given Instant Win game. Will overwrite the
 * previous demo session.
 *
 * @param game The InstantWin game the demo session is for
 * @param plays The number of plays the demo session will create tickets for
 * @returns A game session
 */
const createSession = (
  game: InstantWinDraw,
  plays = 10
): GameSessionPageData => {
  // Don't mutate the prizes in the game object
  const prizeList = game.prizes.slice()

  // For game demos we want to show the highest tier/value prizes first.
  sortPrizes(prizeList, {
    availability: false,
    prizeTier: true,
    cashAltValue: true,
  })

  return GameLocalSession.createSession(game, plays, {
    sessionId: GAME_SESSION_ID,
    ticketPrefix: 'DEMO',
    winProbability: 1,
    prizeOrder: prizeList.map((prize) => prize.prizes_code),
  })
}

/**
 * Returns the current demo game session
 */
const getSession = async (): Promise<GameSessionPageData> => {
  // Simulate a API round-trip delay.
  await delay(150)
  return GameLocalSession.getSession(GAME_SESSION_ID)
}

/**
 * Plays the currently configured game the specified number of times
 *
 * @param playCount The number of draws to play
 */
const playGame = async (playCount: number = 1): Promise<GamePlayData> => {
  // Demo mode doesn't hit the network, however some games account for HTTP
  // latency during play (disabling buttons, playing animations etc). For
  // consistency between demo / live play, simulate a network delay.
  await delay(150)
  return GameLocalSession.playGame(GAME_SESSION_ID, playCount)
}

export const GameDemoSession = {
  createSession,
  playGame,
  getSession,
  demoSessionId: GAME_SESSION_ID,
}
